import React, { useState } from 'react';
import { Message } from '../../types/messages';
import { useMessageUrgency } from '../../hooks/useMessageUrgency';
import { messagesService } from '../../services/messagesService';
import { ReplyType } from '../../types/messages';
import { notify } from '../../lib/globalNotify';
import { sendToTerminal } from '../../lib/terminalLogger';

const LOG_PREFIX = '[MESSAGE_DETAIL]';

interface MessageDetailProps {
  message: Message;
  onMarkAsRead: (messageId: number) => Promise<any>;
  onMessageUpdated: () => void;
}

/**
 * Detalhe da mensagem - Design clean inspirado em PrimeVue Dialog
 */
export function MessageDetail({
  message,
  onMarkAsRead,
  onMessageUpdated
}: MessageDetailProps) {
  const { isResponseDeadlineExpired } = useMessageUrgency();
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyType, setReplyType] = useState<ReplyType>('acknowledgment');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deadlineExpired = isResponseDeadlineExpired(message);
  const needsReply = message.requer_resposta && !message.resposta_enviada;
  const isRead = message.lida;

  // Status
  const getStatus = () => {
    if (deadlineExpired) return 'expired';
    if (needsReply && !isRead) return 'action';
    if (!isRead) return 'new';
    return 'read';
  };

  // Ícone baseado no status
  const getIcon = () => {
    const status = getStatus();
    switch (status) {
      case 'expired': return '⚠️';
      case 'action': return '📩';
      case 'new': return '✉️';
      default: return '📧';
    }
  };

  // Formatar data completa
  const formatFullDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleMarkAsRead = async () => {
    if (isRead || isMarkingAsRead) return;
    try {
      setIsMarkingAsRead(true);
      await onMarkAsRead(message.id);
      onMessageUpdated();
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() && message.resposta_obrigatoria) {
      setError('Resposta é obrigatória');
      return;
    }

    try {
      setIsSending(true);
      setError(null);

      await messagesService.sendReply(message.id, {
        mensagem: replyText.trim() || 'Confirmado',
        tipo: replyType
      });

      notify({
        type: 'success',
        title: 'Resposta enviada',
        message: 'Sua resposta foi enviada com sucesso.'
      });

      sendToTerminal(`✅ ${LOG_PREFIX} Resposta enviada`, { messageId: message.id });

      setReplyText('');
      onMessageUpdated();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao enviar resposta';
      setError(errorMessage);
      notify({
        type: 'error',
        title: 'Erro',
        message: errorMessage
      });
    } finally {
      setIsSending(false);
    }
  };

  const status = getStatus();

  return (
    <>
      {/* Header */}
      <header className="mm-detail-header">
        <div className={`mm-detail-icon ${status}`}>
          <span style={{ fontSize: '1.25rem' }}>{getIcon()}</span>
        </div>

        <div className="mm-detail-title-section">
          <h3 className="mm-detail-title">{message.titulo}</h3>
          <div className="mm-detail-meta">
            {message.criada_por_nome && (
              <span className="mm-detail-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {message.criada_por_nome}
              </span>
            )}
            <span className="mm-detail-meta-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formatFullDate(message.criada_em)}
            </span>
            {needsReply && !deadlineExpired && (
              <span className="mm-tag action">Requer resposta</span>
            )}
            {deadlineExpired && (
              <span className="mm-tag expired">Expirado</span>
            )}
            {message.resposta_enviada && (
              <span className="mm-tag done">Respondida</span>
            )}
          </div>
        </div>

      </header>

      {/* Body */}
      <div className="mm-detail-body">
        <p className="mm-detail-content">{message.mensagem}</p>

        {/* Reply Section */}
        {needsReply && !deadlineExpired && (
          <div className="mm-reply">
            <label className="mm-reply-label">
              Sua resposta {message.resposta_obrigatoria && <span className="mm-required">*</span>}
            </label>
            <textarea
              value={replyText}
              onChange={(e) => {
                setReplyText(e.target.value);
                setError(null);
              }}
              placeholder={message.resposta_obrigatoria ? 'Digite sua resposta...' : 'Resposta (opcional)...'}
              className="mm-reply-input"
              rows={3}
              disabled={isSending}
            />

            {error && <p className="mm-reply-error">{error}</p>}

            <div className="mm-reply-footer">
              <div className="mm-reply-type">
                <label>Tipo:</label>
                <select
                  value={replyType}
                  onChange={(e) => setReplyType(e.target.value as ReplyType)}
                  className="mm-reply-select"
                  disabled={isSending}
                >
                  <option value="acknowledgment">Ciência</option>
                  <option value="confirmation">Confirmação</option>
                  <option value="question">Pergunta</option>
                </select>
              </div>

              <div className="mm-reply-actions">
                <button
                  onClick={handleSendReply}
                  disabled={isSending || (message.resposta_obrigatoria && !replyText.trim())}
                  className="mm-btn"
                >
                  {isSending ? 'Enviando...' : 'Enviar Resposta'}
                </button>
              </div>
            </div>
          </div>
        )}

        {deadlineExpired && needsReply && (
          <div className="mm-expired-alert">
            <p>O prazo para responder esta mensagem expirou.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="mm-detail-footer">
        <div className="mm-detail-footer-info">
          {message.expira_em && (
            <span>
              {deadlineExpired ? 'Expirou em' : 'Expira em'}: {formatFullDate(message.expira_em)}
            </span>
          )}
        </div>

        {!isRead && (
          <button
            onClick={handleMarkAsRead}
            disabled={isMarkingAsRead}
            className="mm-btn mm-btn-secondary"
          >
            {isMarkingAsRead ? 'Marcando...' : 'Marcar como lida'}
          </button>
        )}
      </footer>
    </>
  );
}
